import { supabase } from '@/integrations/supabase/client';
import type { StorageUsage, StorageFile } from '@/types/storageQuota';

export async function fetchStorageUsage(): Promise<StorageUsage> {
  const { data: { user } } = await supabase.auth.getUser();
  if (!user) throw new Error('Usuário não autenticado');

  const { data, error } = await supabase.rpc('get_user_storage_usage', {
    p_user_id: user.id
  });

  if (error) throw error;
  
  // Parse the JSONB response
  const usage = data as unknown as StorageUsage;
  return {
    used_bytes: Number(usage.used_bytes) || 0,
    used_mb: Number(usage.used_mb) || 0,
    limit_mb: Number(usage.limit_mb) || 10,
    limit_bytes: Number(usage.limit_bytes) || 10485760,
    remaining_bytes: Number(usage.remaining_bytes) || 10485760,
    percentage_used: Number(usage.percentage_used) || 0,
  };
}

export async function canUploadFile(fileSizeBytes: number): Promise<boolean> {
  const { data: { user } } = await supabase.auth.getUser();
  if (!user) return false;

  const { data, error } = await supabase.rpc('can_upload_to_forja', {
    p_user_id: user.id,
    p_file_size_bytes: fileSizeBytes
  });

  if (error) {
    console.error('Error checking upload permission:', error);
    return false;
  }

  // Parse the JSONB response
  const result = data as unknown as { can_upload: boolean };
  return result?.can_upload ?? false;
}

export async function fetchStorageFiles(): Promise<StorageFile[]> {
  const { data: { user } } = await supabase.auth.getUser();
  if (!user) throw new Error('Usuário não autenticado');

  const { data, error } = await supabase.rpc('get_user_storage_files', {
    p_user_id: user.id
  });

  if (error) throw error;
  
  return (data as StorageFile[]) || [];
}

export async function deleteStorageFile(filePath: string): Promise<void> {
  const { error } = await supabase.storage
    .from('forja-notes-images')
    .remove([filePath]);

  if (error) throw error;
}

// Extrair URLs de imagens do conteúdo HTML de uma nota
export function extractImageUrls(content: string): string[] {
  const imgRegex = /<img[^>]+src="([^"]+)"/g;
  const urls: string[] = [];
  let match;
  
  while ((match = imgRegex.exec(content)) !== null) {
    urls.push(match[1]);
  }
  
  return urls;
}

// Extrair path do arquivo a partir da URL pública
export function extractFilePathFromUrl(url: string): string | null {
  // URL format: https://...supabase.co/storage/v1/object/public/forja-notes-images/user-id/filename
  const match = url.match(/forja-notes-images\/(.+)$/);
  return match ? match[1] : null;
}
